import { type ChangeEvent, type FC, type FocusEvent, useCallback, useEffect, useId, useRef, useState } from "react";

import { createPortal } from "react-dom";
import { isReferencedNodeValue } from "@doitintl/cloudflow-commons";
import { type ReferencedNodeValue, type ReferencedTextAreaValue } from "@doitintl/cmp-models";
import { FormControl, FormHelperText, InputLabel, OutlinedInput } from "@mui/material";
import isEqual from "lodash/isEqual";

import { useToggle } from "../../../../utils/useToggle";
import { ReferencedFieldAddButton } from "../../CloudflowBuilder/ApiActionParametersForm/parameters/wrappers/ReferencedField/ReferencedFieldAddButton";
import { ReferencedFieldChip } from "../../CloudflowBuilder/ApiActionParametersForm/parameters/wrappers/ReferencedField/ReferencedFieldChip";
import { ReferencedFieldContextProvider } from "../../CloudflowBuilder/ApiActionParametersForm/parameters/wrappers/ReferencedField/ReferencedFieldContextProvider";
import { ReferencedFieldMenu } from "../../CloudflowBuilder/ApiActionParametersForm/parameters/wrappers/ReferencedField/ReferencedFieldMenu";
import { Wrapper } from "../../CloudflowBuilder/ApiActionParametersForm/parameters/wrappers/ReferencedField/ReferencedFieldWrapper";
import {
  type NodeWitOutputModel,
  useReferencedFieldContext,
} from "../../CloudflowBuilder/ApiActionParametersForm/parameters/wrappers/ReferencedField/useReferencedFieldContext";
import {
  type MenuItemData,
  useReferencedFieldMenuItems,
} from "../../CloudflowBuilder/ApiActionParametersForm/parameters/wrappers/ReferencedField/useReferencedFieldMenuItems";
import { InputWithChips } from "./InputWIthChips";
import { type InputWithChipsElement, ReferencedTextAreaChangeEvent } from "./ReferencedTextAreaChangeEvent";

function isSpanElement(node: Node): node is HTMLSpanElement {
  return node.nodeName.toLowerCase() === "span";
}

function translateChildNodesToValue(
  childNodes: NodeListOf<ChildNode>,
  chips: ChipsMap
): (string | ReferencedNodeValue)[] {
  return Array.from(childNodes).flatMap<string | ReferencedNodeValue>((node) => {
    switch (true) {
      case isSpanElement(node) && chips.has(node): {
        const chipPayload = chips.get(node);
        return chipPayload?.value.referencedNodeId ? [chipPayload.value] : [];
      }
      case node.nodeName.toLowerCase() === "br":
        return ["\n"];
      case node.textContent !== null && node.textContent !== "":
        return [node.textContent];
      default:
        return [];
    }
  });
}

function translateValueToChildNodes(editableDivEl: InputWithChipsElement, value: ReferencedTextAreaValue) {
  editableDivEl.innerHTML = "";
  const chipsMap = new Map<HTMLSpanElement, ChipPayload>();
  value.forEach((valueToken) => {
    if (isReferencedNodeValue(valueToken)) {
      const newChipNode = createChipNode();
      editableDivEl.appendChild(newChipNode);
      chipsMap.set(newChipNode, {
        value: valueToken,
        key: crypto.randomUUID(),
      });
    } else {
      valueToken
        .split("\n")
        .reduce((result, text, index, allTokens) => {
          result.push(document.createTextNode(text));
          if (index < allTokens.length - 1) {
            result.push(document.createElement("br"));
          }
          return result;
        }, [] as Node[])
        .forEach((node) => editableDivEl.appendChild(node));
    }
  });

  return chipsMap;
}

function createChipNode() {
  const newNode = document.createElement("span");
  newNode.contentEditable = "false";
  return newNode;
}

type ChipPayload = {
  value: ReferencedNodeValue;
  key: string;
};

type ChipsMap = Map<HTMLSpanElement, ChipPayload>;

type ReferencedTextAreaProps = Partial<{
  label: string;
  name: string;
  helperText: string;
  error: boolean;
  required: boolean;
  value: ReferencedTextAreaValue;
  onChange?: (e: ChangeEvent<InputWithChipsElement>) => void;
  onBlur?: (e: FocusEvent<InputWithChipsElement>) => void;
  rows?: number;
  maxRows?: number;
}>;

export const ReferencedTextArea: FC<ReferencedTextAreaProps> = ({
  label,
  name,
  helperText,
  error,
  required,
  value: outerValue,
  onBlur,
  onChange,
  rows = 4,
  maxRows = 8,
}) => {
  const id = useId();
  const editableDivEl = useRef<InputWithChipsElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [range, setRange] = useState<Range>();
  const chips = useRef<ChipsMap>(new Map());
  const [value, setValue] = useState<ReferencedTextAreaValue>([]);

  const [selectedChip, setSelectedChip] = useState<HTMLSpanElement>();

  const [isMenuShown, showMenu, hideMenu] = useToggle(false);
  const { menuItems, currentlyDisplayedPath } = useReferencedFieldMenuItems(
    selectedChip ? chips.current.get(selectedChip)?.value : undefined
  );
  const { referenceableNodes } = useReferencedFieldContext();

  const dispatchOnChange = () => {
    if (!editableDivEl.current) {
      return;
    }

    const value = translateChildNodesToValue(editableDivEl.current.childNodes, chips.current);
    setValue(value);
    onChange?.(new ReferencedTextAreaChangeEvent(editableDivEl.current, value));
  };

  const dispatchOnBlur = (event: FocusEvent<InputWithChipsElement, Element>) => {
    window.requestAnimationFrame(() => {
      if (!selectedChip && wrapperRef.current && !wrapperRef.current.contains(document.activeElement)) {
        onBlur?.(event);
      }
    });
  };

  const dispatchInputEvent = () => {
    const inputEvent = new Event("input", { bubbles: true });
    editableDivEl.current?.dispatchEvent(inputEvent);
  };

  const dispatchBlurEvent = () => {
    const blurEvent = new Event("blur", { bubbles: true });
    editableDivEl.current?.dispatchEvent(blurEvent);
  };

  const onHideMenu = () => {
    hideMenu();
    setSelectedChip(undefined);
    window.requestAnimationFrame(() => {
      editableDivEl.current?.focus();
      window.requestAnimationFrame(dispatchBlurEvent);
    });
  };

  const onAppendMenuItem = (menuItem: MenuItemData) => {
    if (!selectedChip) {
      return;
    }

    const selectedChipValue = chips.current.get(selectedChip);
    if (menuItem.type === "node") {
      const value = {
        referencedNodeId: menuItem.nodeId,
        referencedField: [],
      };
      if (!selectedChipValue) {
        chips.current.set(selectedChip, {
          value,
          key: crypto.randomUUID(),
        });
      } else {
        selectedChipValue.value = value;
      }
    } else if (selectedChipValue) {
      selectedChipValue.value.referencedField = [...currentlyDisplayedPath, menuItem.name];
    }

    if (!menuItem.hasContinuation) {
      onHideMenu();
    }

    dispatchInputEvent();
  };

  const onPopMenuItem = () => {
    if (!selectedChip) {
      return;
    }

    if (currentlyDisplayedPath.length > 0) {
      const referencedField = Array.from(currentlyDisplayedPath);
      referencedField.length -= 1;
      const selectedChipValue = chips.current.get(selectedChip);
      if (selectedChipValue) {
        selectedChipValue.value.referencedField = referencedField;
      }
    } else {
      chips.current.delete(selectedChip);
    }

    dispatchInputEvent();
  };

  const onClickAddButton = useCallback(() => {
    const newChipNode = createChipNode();

    if (range) {
      range.deleteContents();
      range.insertNode(newChipNode);
    } else if (editableDivEl.current) {
      editableDivEl.current.appendChild(newChipNode);
    }

    const selection = window.getSelection();

    if (newChipNode.nextSibling && selection) {
      const newRange = document.createRange();
      selection.removeAllRanges();
      newRange.selectNode(newChipNode.nextSibling);
      newRange.collapse(true);
      selection.addRange(newRange);
    }

    setSelectedChip(newChipNode);
    showMenu();
  }, [range, showMenu]);

  useEffect(() => {
    if (editableDivEl.current && !isEqual(outerValue, value)) {
      chips.current = translateValueToChildNodes(editableDivEl.current, outerValue ?? []);
      dispatchInputEvent();
    }
  }, [outerValue, value]);

  return (
    <Wrapper ref={wrapperRef}>
      <FormControl fullWidth error={error} required={required}>
        <InputLabel htmlFor={id} shrink>
          {label}
        </InputLabel>
        <OutlinedInput
          id={id}
          aria-describedby={`${id}-helper-text`}
          label={label}
          name={name}
          error={error}
          inputComponent={InputWithChips}
          inputRef={editableDivEl}
          notched
          sx={{ pr: 0 }}
          inputProps={{
            name,
            rows,
            maxRows,
            onBlur: (event) => {
              setRange(window.getSelection()?.getRangeAt(0));
              // clean up chips that are not connected to the DOM anymore
              Array.from(chips.current.keys())
                .filter((chip) => !chip.isConnected)
                .forEach((chip) => {
                  chips.current.delete(chip);
                });

              dispatchOnBlur(event as unknown as FocusEvent<InputWithChipsElement, Element>);
            },
            onInput: dispatchOnChange,
          }}
          endAdornment={
            <ReferencedFieldAddButton
              sx={{
                height: "100%",
                borderWidth: "0px 0px 0px 1px",
              }}
              onClick={onClickAddButton}
            />
          }
        />

        {helperText && <FormHelperText id={`${id}-helper-text`}>{helperText}</FormHelperText>}
      </FormControl>

      <ReferencedFieldMenu
        open={isMenuShown}
        anchorEl={editableDivEl.current}
        hideBackButton={false}
        onClose={onHideMenu}
        menuItems={menuItems}
        onAppendMenuItem={onAppendMenuItem}
        onPopMenuItem={onPopMenuItem}
      />
      {Array.from(chips.current.entries()).map(([chip, { key, value }]) =>
        createPortal(
          <ReferencedFieldChip
            key={key}
            referenceableNodes={referenceableNodes}
            value={value}
            onDelete={() => {
              chip.remove();
              chips.current.delete(chip);
              dispatchOnChange();
            }}
            onClick={() => {
              setSelectedChip(chip);
              showMenu();
            }}
            onChange={(value) => {
              chips.current.set(chip, {
                value,
                key,
              });
              dispatchOnChange();
            }}
            selected={chip === selectedChip}
          />,
          chip
        )
      )}
    </Wrapper>
  );
};

export const ReferencedTextAreaStandalone: FC<
  ReferencedTextAreaProps & {
    referenceableNodes: NodeWitOutputModel[];
  }
> = ({ referenceableNodes, ...inputProps }) => (
  <ReferencedFieldContextProvider referenceableNodes={referenceableNodes}>
    <ReferencedTextArea {...inputProps} />
  </ReferencedFieldContextProvider>
);
